import { Box } from '@zoopla-eng/zds/box';
import { Divider } from '@zoopla-eng/zds/divider';
import { Link } from '@zoopla-eng/zds/link';
import { triggerTracking } from '@zoopla/tracking';
import {
    cardTitleStyle,
    countryLinksStyle,
    seoSideLinkCardStyle,
    seoSideLinkContainerStyle
} from './SeoSideLinks.styles.css';

type SeoSideLinksProps = { areaName: string; slug: string };

const CardLink: React.FC<
    React.PropsWithChildren<{
        href: string;
        onClick: () => void;
    }>
> = ({ href, children, onClick }) => (
    <Link href={href} className="cardLink" onClick={onClick}>
        {children}
    </Link>
);

const trackingClickFactory = (eventLabel: string) =>
    triggerTracking({
        event: 'GAEvent',
        eventAction: 'SEO widget click',
        eventCategory: 'search results',
        eventLabel
    });

export const COUNTRY_TO_REGION_LINKS: {
    [key: string]: { linkTitle: string; href: string }[];
} = {
    UK: [
        {
            linkTitle: 'House Prices in England',
            href: '/house-prices/england/'
        },
        {
            linkTitle: 'House Prices in Scotland',
            href: '/house-prices/scotland/'
        },
        {
            linkTitle: 'House Prices in Wales',
            href: '/house-prices/wales/'
        }
    ],
    England: [
        {
            linkTitle: 'House Prices in London',
            href: '/house-prices/london/'
        },
        {
            linkTitle: 'House Prices in South East',
            href: '/house-prices/south-east-england/'
        },
        {
            linkTitle: 'House Prices in East Midlands',
            href: '/house-prices/east-midlands/'
        },
        {
            linkTitle: 'House Prices in East England',
            href: '/house-prices/east-of-england/'
        },
        {
            linkTitle: 'House Prices in North East',
            href: '/house-prices/north-east-england/'
        },
        {
            linkTitle: 'House Prices in North West',
            href: '/house-prices/north-west-england/'
        },
        {
            linkTitle: 'House Prices in South West',
            href: '/house-prices/south-west-england/'
        },
        {
            linkTitle: 'House Prices in West Midlands',
            href: '/house-prices/west-midlands/'
        },
        {
            linkTitle: 'House Prices in Yorkshire and The Humber',
            href: '/house-prices/yorkshire-and-the-humber/'
        }
    ],
    Scotland: [
        {
            linkTitle: 'House Prices in Edinburgh',
            href: '/house-prices/edinburgh/'
        },
        {
            linkTitle: 'House Prices in Glasgow',
            href: '/house-prices/glasgow/'
        },
        {
            linkTitle: 'House Prices in Aberdeen',
            href: '/house-prices/aberdeen/'
        },
        {
            linkTitle: 'House Prices in Dundee',
            href: '/house-prices/dundee/'
        },
        {
            linkTitle: 'House Prices in Stirling',
            href: '/house-prices/stirling/'
        },
        {
            linkTitle: 'House Prices in Shetland',
            href: '/house-prices/shetland/'
        },
        {
            linkTitle: 'House Prices in Falkirk',
            href: '/house-prices/falkirk/'
        },
        {
            linkTitle: 'House Prices in Fife',
            href: '/house-prices/fife/'
        },
        {
            linkTitle: 'House Prices in Orkney',
            href: '/house-prices/orkney/'
        }
    ],
    Wales: [
        {
            linkTitle: 'House Prices in Cardiff',
            href: '/house-prices/cardiff/'
        },
        {
            linkTitle: 'House Prices in Swansea',
            href: '/house-prices/swansea/'
        },
        {
            linkTitle: 'House Prices in Newport',
            href: '/house-prices/wales/newport/'
        },
        {
            linkTitle: 'House Prices in Wrexham',
            href: '/house-prices/wrexham/'
        },
        {
            linkTitle: 'House Prices in Bridgend',
            href: '/house-prices/bridgend-county/'
        },
        {
            linkTitle: 'House Prices in Llandudno',
            href: '/house-prices/llandudno/'
        },
        {
            linkTitle: 'House Prices in Pembrokeshire',
            href: '/house-prices/pembrokeshire/'
        },
        {
            linkTitle: 'House Prices in Penarth',
            href: '/house-prices/penarth/'
        },
        {
            linkTitle: 'House Prices in Caerphilly',
            href: '/house-prices/caerphilly/'
        }
    ]
};

const SeoSideLinks = ({ areaName, slug }: SeoSideLinksProps) => {
    const suggestedSearches = [
        {
            linkTitle: `Properties for sale in ${areaName}`,
            href: `/for-sale/property/${slug}`
        },
        {
            linkTitle: `Properties for rent in ${areaName}`,
            href: `/to-rent/property/${slug}`
        },
        {
            linkTitle: `New homes for sale in ${areaName}`,
            href: `/new-homes/property/${slug}`
        }
    ];
    const isCountryResult = Object.keys(COUNTRY_TO_REGION_LINKS).includes(
        areaName
    );

    const genericLinks = [
        {
            linkTitle: 'Get an instant valuation',
            href: '/home-values/'
        },
        {
            linkTitle: 'Get an agent valuation',
            href: '/valuation/'
        },
        {
            linkTitle: 'Read our selling guide',
            href: '/selling-a-home/'
        },
        {
            linkTitle: 'Read our buying guide',
            href: '/buying-a-home/'
        }
    ];

    return (
        <div className={seoSideLinkContainerStyle}>
            {isCountryResult && (
                <div className={seoSideLinkCardStyle}>
                    <span className={cardTitleStyle}>
                        House prices in {areaName}
                    </span>
                    <Box pt="S300" pb="S300">
                        <Divider direction="horizontal" />
                    </Box>
                    <div className={countryLinksStyle}>
                        {COUNTRY_TO_REGION_LINKS[areaName].map(
                            ({ linkTitle, href }, i) => (
                                <CardLink
                                    key={i}
                                    href={href}
                                    onClick={() =>
                                        trackingClickFactory(linkTitle)
                                    }
                                >
                                    {linkTitle}
                                </CardLink>
                            )
                        )}
                    </div>
                </div>
            )}
            <div className={seoSideLinkCardStyle}>
                <span className={cardTitleStyle}>Suggested searches</span>
                <Box pt="S300" pb="S300">
                    <Divider direction="horizontal" />
                </Box>
                {suggestedSearches.map(({ linkTitle, href }, i) => (
                    <CardLink
                        key={i}
                        href={href}
                        onClick={() => trackingClickFactory(linkTitle)}
                    >
                        {linkTitle}
                    </CardLink>
                ))}
            </div>
            <div className={seoSideLinkCardStyle}>
                <span className={cardTitleStyle}>What's your home worth?</span>
                <Box pt="S300" pb="S300">
                    <Divider direction="horizontal" />
                </Box>
                {genericLinks.map(({ linkTitle, href }, i) => (
                    <CardLink
                        key={i}
                        href={href}
                        onClick={() => trackingClickFactory(linkTitle)}
                    >
                        {linkTitle}
                    </CardLink>
                ))}
            </div>
        </div>
    );
};

export default SeoSideLinks;
